import api from './api';
import type { TenantConfig } from '@/types/tenant';

export interface CompanyResponse {
  success: boolean;
  data: TenantConfig;
  message?: string;
}

export interface CompanyDetailsResponse {
  success: boolean;
  data: TenantConfig & {
    stats?: {
      totalUsers: number;
      activeUsers: number;
      totalProperties: number;
      availableProperties: number;
      totalLeads: number;
      activeLeads: number;
      totalBlogPosts: number;
      featuredPosts: number;
    };
  };
  message?: string;
}

export interface SlugAvailabilityResponse {
  success: boolean;
  data: {
    slug: string;
    available: boolean;
  };
  message?: string;
}

class CompanyService {
  /**
   * Get company by slug (PUBLIC endpoint)
   */
  async getCompanyBySlug(slug: string): Promise<TenantConfig> {
    try {
      const response = await api.get<CompanyResponse>(`/companies/public/${slug}`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch company');
      }

      return this.mapCompanyToTenant(response.data.data);
    } catch (error) {
      console.error('Error fetching company by slug:', error);
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Failed to fetch company configuration'
      );
    }
  }

  /**
   * Get company details by slug (PUBLIC endpoint)
   */
  async getCompanyDetailsBySlug(slug: string): Promise<TenantConfig & { stats?: any }> {
    try {
      const response = await api.get<CompanyDetailsResponse>(`/companies/public/${slug}/details`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch company details');
      }

      return {
        ...this.mapCompanyToTenant(response.data.data),
        stats: response.data.data.stats
      };
    } catch (error) {
      console.error('Error fetching company details by slug:', error);
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Failed to fetch company details'
      );
    }
  }

  /**
   * Check if slug is available (PUBLIC endpoint)
   */
  async checkSlugAvailability(slug: string, excludeId?: string): Promise<boolean> {
    try {
      const params = excludeId ? { excludeId } : {};
      const response = await api.get<SlugAvailabilityResponse>(
        `/companies/check-slug/${slug}`, 
        { params }
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to check slug availability');
      }

      return response.data.data.available;
    } catch (error) {
      console.error('Error checking slug availability:', error);
      // En caso de error, asumimos que no está disponible por seguridad
      return false;
    }
  }

  /**
   * Get current user's company (PRIVATE endpoint)
   */
  async getCurrentCompany(): Promise<TenantConfig> {
    try {
      const response = await api.get<CompanyResponse>('/companies/current');
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch current company');
      }

      return this.mapCompanyToTenant(response.data.data);
    } catch (error) {
      console.error('Error fetching current company:', error);
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Failed to fetch current company'
      );
    }
  }

  /**
   * Map backend company response to frontend TenantConfig
   */
  private mapCompanyToTenant(company: any): TenantConfig {
    return {
      id: company.id,
      name: company.name,
      slug: company.slug,
      logo: company.logo || `/assets/${company.slug}/logo.svg`,
      favicon: company.favicon || `/assets/${company.slug}/favicon.ico`,
      primaryColor: company.primaryColor,
      secondaryColor: company.secondaryColor,
      accentColor: company.accentColor,
      contact: {
        phone: company.contact?.phone || '',
        email: company.contact?.email || '',
        address: company.contact?.address || '',
        whatsapp: company.contact?.whatsapp || ''
      },
      social: {
        facebook: company.social?.facebook || '',
        instagram: company.social?.instagram || '',
        twitter: company.social?.twitter || '',
        linkedin: company.social?.linkedin || '',
        youtube: company.social?.youtube || ''
      },
      seo: {
        title: company.seo?.title || `${company.name} - Real Estate`,
        description: company.seo?.description || `Find your perfect property with ${company.name}`,
        keywords: company.seo?.keywords || ['real estate', 'properties'],
        ogImage: company.seo?.ogImage || `/assets/${company.slug}/og-image.jpg`
      },
      features: {
        blog: company.features?.blog ?? true,
        multiLanguage: company.features?.multiLanguage ?? false,
        propertyComparison: company.features?.propertyComparison ?? true,
        virtualTours: company.features?.virtualTours ?? true
      },
      currencies: company.currencies || ['USD'],
      languages: company.languages || ['es'],
      theme: {
        mode: (company.theme?.mode as 'light' | 'dark' | 'auto') || 'light',
        style: company.theme?.style || 'modern'
      }
    };
  }

  /**
   * Get default company slug from URL or environment
   */
  getDefaultCompanySlug(): string {
    const hostname = window.location.hostname;
    const isLocal = hostname === 'localhost' || hostname === '127.0.0.1' || !hostname.includes('.');

    // PROD: subdominio manda siempre (ej: luxury.redraiz.co → "luxury")
    if (!isLocal) {
      const subdomain = hostname.split('.')[0];
      if (subdomain && subdomain !== 'www' && !subdomain.match(/^\d+$/)) {
        return subdomain;
      }
    }

    // DEV/iframe: query param ?tenant=<slug>
    const qsTenant = new URLSearchParams(window.location.search).get('tenant');
    if (qsTenant) return qsTenant;

    // DEV build-time env (VITE_TENANT)
    const envTenant = import.meta.env.VITE_TENANT as string | undefined;
    if (envTenant) return envTenant;

    // Path-based: /<slug>
    const pathSegments = window.location.pathname.split('/').filter(Boolean);
    if (pathSegments.length > 0 && !['admin', 'login', 'register'].includes(pathSegments[0])) {
      return pathSegments[0];
    }

    return import.meta.env.VITE_DEFAULT_COMPANY_SLUG || 'elite';
  }
}

export const companyService = new CompanyService();
export default companyService;